// Phase 5.9 · 판매자 공동구매 + 쿠폰 관리 페이지 (통합)
// 기존 SellerDashboard/OMS 무변경. Route `/seller/groupbuy-coupon` 로 접근.
import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, X, Send, Package, Ticket, Users, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;
const badge = (s) => ({
  scheduled:      'bg-gray-200 text-gray-700',
  live:           'bg-[#FF3B30] text-white',
  target_reached: 'bg-emerald-600 text-white',
  ended:          'bg-zinc-500 text-white',
  cancelled:      'bg-orange-500 text-white',
  active:         'bg-emerald-500/10 text-emerald-700',
  disabled:       'bg-gray-200 text-gray-500',
}[s] || 'bg-gray-100 text-gray-500');

// ---- Group Buy Form Modal --------------------------------------------------
function GroupBuyModal({ open, onClose, onSaved }) {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ product_id: '', mode: 'amount', base_price: 0,
    target: 30, allow_coupon: false, tiers: [{ min_participants: 10, discount: 2000 }] });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get('/seller/products').then((r) => {
      const arr = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setProducts(arr);
      if (arr.length && !form.product_id) setForm((f) => ({ ...f, product_id: arr[0].product_id, base_price: arr[0].sale_price || 0 }));
    }).catch(() => setProducts([]));
    // eslint-disable-next-line
  }, [open]);

  if (!open) return null;
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setTier = (i, k, v) => setForm((f) => ({ ...f, tiers: f.tiers.map((t, idx) => idx === i ? { ...t, [k]: v } : t) }));
  const addTier = () => setForm((f) => f.tiers.length >= 5 ? f : ({ ...f, tiers: [...f.tiers, { min_participants: 0, discount: 0 }] }));
  const removeTier = (i) => setForm((f) => ({ ...f, tiers: f.tiers.filter((_, idx) => idx !== i) }));

  const submit = async () => {
    if (!form.product_id) { toast.error('상품을 선택해주세요.'); return; }
    setBusy(true);
    try {
      await api.post('/seller/group-buys', {
        product_id: form.product_id,
        mode: form.mode,
        base_price: Number(form.base_price) || 0,
        target: Number(form.target) || 0,
        allow_coupon: form.allow_coupon,
        tiers: form.tiers.map((t) => ({ min_participants: Number(t.min_participants) || 0, discount: Number(t.discount) || 0 })),
      });
      toast.success('공동구매 생성 완료');
      onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || '생성 실패'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="gb-create-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-lg max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-white dark:bg-[#141414] shadow-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-black">공동구매 생성</h3>
          <button onClick={onClose} className="p-2 hover:bg-black/5 rounded"><X size={18}/></button>
        </div>
        <div className="space-y-3 text-sm">
          <label className="block"><span className="text-xs font-bold text-gray-500">대상 상품</span>
            <select value={form.product_id} onChange={(e) => {
              const p = products.find((x) => x.product_id === e.target.value);
              setField('product_id', e.target.value);
              if (p) setField('base_price', p.sale_price || 0);
            }} className="w-full px-3 py-2 rounded border border-black/10 dark:border-white/10 bg-transparent mt-1" data-testid="gb-product-select">
              <option value="">선택하세요</option>
              {products.map((p) => <option key={p.product_id} value={p.product_id}>{p.name}</option>)}
            </select>
          </label>
          <label className="block"><span className="text-xs font-bold text-gray-500">기준 판매가</span>
            <input type="number" value={form.base_price} onChange={(e) => setField('base_price', e.target.value)}
              className="w-full px-3 py-2 rounded border border-black/10 dark:border-white/10 bg-transparent mt-1" data-testid="gb-base-price" />
          </label>
          <label className="block"><span className="text-xs font-bold text-gray-500">목표 인원</span>
            <input type="number" value={form.target} onChange={(e) => setField('target', e.target.value)}
              className="w-full px-3 py-2 rounded border border-black/10 dark:border-white/10 bg-transparent mt-1" data-testid="gb-target" />
          </label>
          <div>
            <span className="text-xs font-bold text-gray-500">할인 방식</span>
            <div className="mt-1 inline-flex rounded-full border border-black/10 dark:border-white/10 overflow-hidden">
              <button onClick={() => setField('mode', 'amount')} className={`px-3 py-1.5 text-xs font-bold ${form.mode === 'amount' ? 'bg-[#FF3B30] text-white' : ''}`} data-testid="gb-mode-amount">금액 할인</button>
              <button onClick={() => setField('mode', 'percent')} className={`px-3 py-1.5 text-xs font-bold ${form.mode === 'percent' ? 'bg-[#FF3B30] text-white' : ''}`} data-testid="gb-mode-percent">% 할인</button>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-gray-500">할인 단계 (최대 5)</span>
              <button onClick={addTier} disabled={form.tiers.length >= 5} className="text-xs font-bold text-[#FF3B30] inline-flex items-center gap-1" data-testid="gb-tier-add"><Plus size={11}/> 단계 추가</button>
            </div>
            <div className="space-y-2">
              {form.tiers.map((t, i) => (
                <div key={i} className="flex items-center gap-2 text-xs" data-testid={`gb-tier-${i}`}>
                  <input type="number" value={t.min_participants} onChange={(e) => setTier(i, 'min_participants', e.target.value)}
                    className="w-20 px-2 py-1 rounded border border-black/10 dark:border-white/10 bg-transparent" placeholder="인원" data-testid={`gb-tier-min-${i}`}/>
                  <span className="text-gray-500">명 →</span>
                  <input type="number" value={t.discount} onChange={(e) => setTier(i, 'discount', e.target.value)}
                    className="flex-1 px-2 py-1 rounded border border-black/10 dark:border-white/10 bg-transparent" placeholder={form.mode === 'percent' ? '%' : '원'} data-testid={`gb-tier-discount-${i}`}/>
                  <span className="text-gray-500">{form.mode === 'percent' ? '%' : '원'}</span>
                  <button onClick={() => removeTier(i)} className="p-1 text-red-500 hover:bg-red-50 rounded" data-testid={`gb-tier-remove-${i}`}><Trash2 size={12}/></button>
                </div>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={form.allow_coupon} onChange={(e) => setField('allow_coupon', e.target.checked)} data-testid="gb-allow-coupon"/>
            공동구매 상품에 쿠폰 사용 허용 (권장: 미체크)
          </label>
        </div>
        <div className="mt-5 flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-sm font-bold border border-black/10">취소</button>
          <button onClick={submit} disabled={busy} className="px-4 py-2 rounded-full text-sm font-black bg-[#FF3B30] text-white disabled:opacity-50" data-testid="gb-submit">생성</button>
        </div>
      </div>
    </div>
  );
}

// ---- Coupon Form Modal -----------------------------------------------------
function CouponModal({ open, onClose, onSaved }) {
  const [form, setForm] = useState({ name: '', kind: 'amount', discount_value: 3000,
    min_order_amount: 0, max_discount: 0, valid_to: '', total_quota: 0, per_user_limit: 1,
    applies_all_products: true, product_ids: [] });
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.name.trim()) { toast.error('쿠폰명을 입력해주세요.'); return; }
    setBusy(true);
    try {
      await api.post('/seller/coupons', {
        ...form,
        discount_value: Number(form.discount_value) || 0,
        min_order_amount: Number(form.min_order_amount) || 0,
        max_discount: Number(form.max_discount) || 0,
        total_quota: Number(form.total_quota) || 0,
        per_user_limit: Number(form.per_user_limit) || 1,
        valid_to: form.valid_to || null,
      });
      toast.success('쿠폰 생성 완료');
      onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || '생성 실패'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="coupon-create-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl bg-white dark:bg-[#141414] shadow-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-black">쿠폰 생성</h3>
          <button onClick={onClose} className="p-2 hover:bg-black/5 rounded"><X size={18}/></button>
        </div>
        <div className="space-y-3 text-sm">
          <input value={form.name} onChange={(e) => setField('name', e.target.value)} placeholder="쿠폰명 (예: LIVE 신규 5,000원 할인)"
            className="w-full px-3 py-2 rounded border border-black/10 dark:border-white/10 bg-transparent" data-testid="coupon-name"/>
          <div>
            <span className="text-xs font-bold text-gray-500">쿠폰 종류</span>
            <div className="mt-1 inline-flex rounded-full border border-black/10 dark:border-white/10 overflow-hidden">
              <button onClick={() => setField('kind', 'amount')} className={`px-3 py-1.5 text-xs font-bold ${form.kind === 'amount' ? 'bg-[#FF3B30] text-white' : ''}`} data-testid="coupon-kind-amount">금액할인</button>
              <button onClick={() => setField('kind', 'percent')} className={`px-3 py-1.5 text-xs font-bold ${form.kind === 'percent' ? 'bg-[#FF3B30] text-white' : ''}`} data-testid="coupon-kind-percent">%할인</button>
              <button onClick={() => setField('kind', 'free_ship')} className={`px-3 py-1.5 text-xs font-bold ${form.kind === 'free_ship' ? 'bg-[#FF3B30] text-white' : ''}`} data-testid="coupon-kind-freeship">무료배송</button>
            </div>
          </div>
          {form.kind !== 'free_ship' && (
            <label className="block"><span className="text-xs font-bold text-gray-500">{form.kind === 'percent' ? '할인율 (%)' : '할인 금액 (원)'}</span>
              <input type="number" value={form.discount_value} onChange={(e) => setField('discount_value', e.target.value)}
                className="w-full px-3 py-2 rounded border border-black/10 dark:border-white/10 bg-transparent mt-1" data-testid="coupon-discount-value"/>
            </label>
          )}
          <label className="block"><span className="text-xs font-bold text-gray-500">최소 주문금액 (원)</span>
            <input type="number" value={form.min_order_amount} onChange={(e) => setField('min_order_amount', e.target.value)}
              className="w-full px-3 py-2 rounded border border-black/10 dark:border-white/10 bg-transparent mt-1" data-testid="coupon-min-order"/>
          </label>
          {form.kind === 'percent' && (
            <label className="block"><span className="text-xs font-bold text-gray-500">최대 할인금액 (원, 0=없음)</span>
              <input type="number" value={form.max_discount} onChange={(e) => setField('max_discount', e.target.value)}
                className="w-full px-3 py-2 rounded border border-black/10 dark:border-white/10 bg-transparent mt-1" data-testid="coupon-max-discount"/>
            </label>
          )}
          <label className="block"><span className="text-xs font-bold text-gray-500">사용 종료일 (선택)</span>
            <input type="datetime-local" value={form.valid_to} onChange={(e) => setField('valid_to', e.target.value)}
              className="w-full px-3 py-2 rounded border border-black/10 dark:border-white/10 bg-transparent mt-1" data-testid="coupon-valid-to"/>
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block"><span className="text-xs font-bold text-gray-500">발급 수량 (0=제한없음)</span>
              <input type="number" value={form.total_quota} onChange={(e) => setField('total_quota', e.target.value)}
                className="w-full px-3 py-2 rounded border border-black/10 dark:border-white/10 bg-transparent mt-1" data-testid="coupon-quota"/>
            </label>
            <label className="block"><span className="text-xs font-bold text-gray-500">1인당 발급</span>
              <input type="number" value={form.per_user_limit} onChange={(e) => setField('per_user_limit', e.target.value)}
                className="w-full px-3 py-2 rounded border border-black/10 dark:border-white/10 bg-transparent mt-1" data-testid="coupon-per-user"/>
            </label>
          </div>
        </div>
        <div className="mt-5 flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-sm font-bold border border-black/10">취소</button>
          <button onClick={submit} disabled={busy} className="px-4 py-2 rounded-full text-sm font-black bg-[#FF3B30] text-white disabled:opacity-50" data-testid="coupon-submit">생성</button>
        </div>
      </div>
    </div>
  );
}

// ---- Issue Coupon Modal (Customer 360 재사용 대체 · 여기선 user_id 리스트로 발급) ----
function IssueCouponModal({ open, onClose, coupon, onIssued }) {
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get('/seller/customers').then((r) => {
      const list = (r.data?.items || []).filter((c) => c.user_id);
      setCustomers(list); setSelected(new Set());
    }).catch(() => setCustomers([]));
  }, [open]);
  if (!open) return null;
  const toggle = (uid) => setSelected((s) => { const n = new Set(s); n.has(uid) ? n.delete(uid) : n.add(uid); return n; });
  const submit = async () => {
    if (selected.size === 0) { toast.error('발급할 고객을 선택해주세요.'); return; }
    setBusy(true);
    try {
      const r = await api.post(`/seller/coupons/${coupon.coupon_id}/issue`,
        { user_ids: Array.from(selected) });
      toast.success(`발급 ${r.data.issued}건 · 스킵 ${r.data.skipped?.length || 0}건`);
      onIssued?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || '발급 실패'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="coupon-issue-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg max-h-[80vh] overflow-y-auto rounded-2xl bg-white dark:bg-[#141414] shadow-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-black">쿠폰 발급 · {coupon.name}</h3>
          <button onClick={onClose} className="p-2 hover:bg-black/5 rounded"><X size={18}/></button>
        </div>
        <p className="text-xs text-gray-500 mb-3">회원 고객 목록에서 다중 선택 후 발급합니다.</p>
        <div className="space-y-1">
          {customers.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">회원 고객이 없습니다.</p>
          ) : customers.map((c) => (
            <label key={c.user_id} className="flex items-center gap-2 p-2 rounded hover:bg-black/5 cursor-pointer text-sm" data-testid={`issue-cust-${c.user_id}`}>
              <input type="checkbox" checked={selected.has(c.user_id)} onChange={() => toggle(c.user_id)}/>
              <span className="font-bold flex-1">{c.name || c.user_id}</span>
              <span className="text-xs text-gray-500">{c.phone || c.email || '-'}</span>
            </label>
          ))}
        </div>
        <div className="mt-4 flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-sm font-bold border border-black/10">취소</button>
          <button onClick={submit} disabled={busy || selected.size === 0} className="px-4 py-2 rounded-full text-sm font-black bg-[#FF3B30] text-white disabled:opacity-40" data-testid="coupon-issue-submit">
            {selected.size}명에게 발급
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Main Page -------------------------------------------------------------
export default function SellerGroupBuyCouponPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('groupbuys');
  const [gbs, setGbs] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [gbOpen, setGbOpen] = useState(false);
  const [couponOpen, setCouponOpen] = useState(false);
  const [issueFor, setIssueFor] = useState(null);

  const load = useCallback(async () => {
    try {
      const [gb, cp] = await Promise.all([
        api.get('/seller/group-buys'),
        api.get('/seller/coupons'),
      ]);
      setGbs(gb.data?.items || []);
      setCoupons(cp.data?.items || []);
    } catch { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const endGb = async (gid) => {
    if (!window.confirm('공동구매를 종료하시겠습니까? 종료 시점의 참여자 수 기준으로 최종 가격이 확정됩니다.')) return;
    try {
      const r = await api.post(`/seller/group-buys/${gid}/end`);
      toast.success(`최종 가격 ${won(r.data.final_price)}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '종료 실패'); }
  };
  const disableCoupon = async (cid) => {
    try { await api.patch(`/seller/coupons/${cid}`, { status: 'disabled' }); toast.success('중단됨'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || '변경 실패'); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="gb-coupon-page">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-black">
        <ArrowLeft size={14}/> 뒤로
      </button>
      <div className="flex items-baseline gap-2">
        <h1 className="text-2xl font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>공동구매 · 쿠폰</h1>
        <span className="text-xs text-gray-500">Phase 5.9</span>
      </div>
      <div className="inline-flex rounded-full border border-black/10 dark:border-white/10 overflow-hidden">
        <button onClick={() => setTab('groupbuys')} className={`px-4 py-2 text-sm font-bold ${tab === 'groupbuys' ? 'bg-[#FF3B30] text-white' : ''}`} data-testid="tab-groupbuys">
          <Package size={12} className="inline mr-1"/> 공동구매 ({gbs.length})
        </button>
        <button onClick={() => setTab('coupons')} className={`px-4 py-2 text-sm font-bold ${tab === 'coupons' ? 'bg-[#FF3B30] text-white' : ''}`} data-testid="tab-coupons">
          <Ticket size={12} className="inline mr-1"/> 쿠폰 ({coupons.length})
        </button>
      </div>

      {tab === 'groupbuys' && (
        <div className="rounded-2xl border border-black/10 dark:border-white/10 p-4" data-testid="gb-section">
          <div className="flex justify-between items-center mb-3">
            <p className="text-sm font-black">진행 중/과거 공동구매</p>
            <button onClick={() => setGbOpen(true)} className="px-3 py-1.5 rounded-full text-xs font-bold bg-[#FF3B30] text-white inline-flex items-center gap-1" data-testid="gb-new-btn">
              <Plus size={12}/> 공동구매 만들기
            </button>
          </div>
          {gbs.length === 0 ? (
            <p className="text-sm text-gray-500 py-8 text-center">아직 공동구매가 없습니다.</p>
          ) : (
            <div className="space-y-2">
              {gbs.map((gb) => (
                <div key={gb.group_buy_id} className="p-3 rounded-lg border border-black/5 dark:border-white/5 flex items-center gap-3 flex-wrap" data-testid={`gb-row-${gb.group_buy_id}`}>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${badge(gb.status)}`}>{gb.status}</span>
                  <span className="font-bold text-sm flex-1 truncate">{gb.product_name}</span>
                  <span className="text-xs text-gray-500 inline-flex items-center gap-1"><Users size={11}/> {gb.current_count}/{gb.target}명</span>
                  <span className="text-xs text-gray-500">{gb.mode === 'percent' ? '%' : '원'} · {gb.tiers?.length || 0}단계</span>
                  <span className="text-xs font-black text-[#FF3B30]">기준가 {won(gb.base_price)}</span>
                  {gb.final_price != null && <span className="text-xs font-black text-emerald-600">최종 {won(gb.final_price)}</span>}
                  {gb.status === 'live' || gb.status === 'target_reached' ? (
                    <button onClick={() => endGb(gb.group_buy_id)} className="px-2 py-1 text-xs font-bold border border-red-300 text-red-600 rounded-full" data-testid={`gb-end-${gb.group_buy_id}`}>종료</button>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'coupons' && (
        <div className="rounded-2xl border border-black/10 dark:border-white/10 p-4" data-testid="coupon-section">
          <div className="flex justify-between items-center mb-3">
            <p className="text-sm font-black">쿠폰 목록</p>
            <button onClick={() => setCouponOpen(true)} className="px-3 py-1.5 rounded-full text-xs font-bold bg-[#FF3B30] text-white inline-flex items-center gap-1" data-testid="coupon-new-btn">
              <Plus size={12}/> 쿠폰 만들기
            </button>
          </div>
          {coupons.length === 0 ? (
            <p className="text-sm text-gray-500 py-8 text-center">쿠폰이 없습니다.</p>
          ) : (
            <div className="space-y-2">
              {coupons.map((c) => (
                <div key={c.coupon_id} className="p-3 rounded-lg border border-black/5 dark:border-white/5 flex items-center gap-3 flex-wrap" data-testid={`coupon-row-${c.coupon_id}`}>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${badge(c.status)}`}>{c.status}</span>
                  <span className="font-bold text-sm flex-1 truncate">{c.name}</span>
                  <span className="text-xs font-black text-[#FF3B30]">
                    {c.kind === 'free_ship' ? '무료배송'
                      : c.kind === 'percent' ? `${c.discount_value}%`
                      : won(c.discount_value)}
                  </span>
                  <span className="text-xs text-gray-500">발급 {c.issued_count || 0} / 사용 {c.used_count || 0}</span>
                  {c.min_order_amount > 0 && <span className="text-xs text-gray-500">최소 {won(c.min_order_amount)}</span>}
                  <button onClick={() => setIssueFor(c)} className="px-2 py-1 text-xs font-bold bg-black text-white rounded-full inline-flex items-center gap-1" data-testid={`coupon-issue-${c.coupon_id}`}>
                    <Send size={11}/> 발급
                  </button>
                  {c.status === 'active' && (
                    <button onClick={() => disableCoupon(c.coupon_id)} className="px-2 py-1 text-xs font-bold border border-gray-300 rounded-full" data-testid={`coupon-disable-${c.coupon_id}`}>중단</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <GroupBuyModal open={gbOpen} onClose={() => setGbOpen(false)} onSaved={load}/>
      <CouponModal open={couponOpen} onClose={() => setCouponOpen(false)} onSaved={load}/>
      {issueFor && <IssueCouponModal open coupon={issueFor} onClose={() => setIssueFor(null)} onIssued={load}/>}
    </div>
  );
}
